import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'

import { VendorsAPI } from '@webapp/api/VendorsAPI'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useProcurifyIntl } from '@webapp/hooks'
import { type Vendor } from '@webapp/models'
import { type IApiError } from '@webapp/services/api'
import { parseAPIError } from '@webapp/utils/errors'
import { VendorDetailLoader } from './components/VendorDetailLoader'
import messages from './messages'
import { VendorDetail } from './VendorDetail'
import { toast } from '../Notification'

export interface IConnectedVendorDetailProps {
  vendorId: Vendor['id']
}

export const ConnectedVendorDetail = ({
  vendorId,
}: IConnectedVendorDetailProps) => {
  const intl = useProcurifyIntl()

  const { enable_vendor_approvals: isVendorApprovalsEnabled } = useSelector(
    makeSelectFeatureSwitches()
  )

  const { data: vendor, isFetched: isVendorFetched } = useQuery(
    ['vendor-detail', { id: vendorId }],
    () => {
      return VendorsAPI.getVendorDetail(vendorId)
        .then((res) => res.data)
        .catch((err) => {
          displayErrorMessage(err, 'vendor')
          return null
        })
    },
    {
      cacheTime: 0,
    }
  )

  const displayErrorMessage = (err: IApiError, type: string) => {
    toast.error({
      title: intl.formatMessage(messages.errorTitleText),
      children: parseAPIError(
        err.data,
        intl.formatMessage(messages.errorMessageText, { type })
      ).message,
    })
  }

  return !isVendorFetched || !vendor ? (
    <VendorDetailLoader />
  ) : (
    <VendorDetail
      vendor={vendor}
      isVendorApprovalsEnabled={isVendorApprovalsEnabled}
    />
  )
}

ConnectedVendorDetail.displayName = 'ConnectedVendorDetail'
